import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Search,
  Plus,
  Edit,
  RefreshCw,
  DollarSign,
  Building2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getAllEmployeeSalaries, upsertEmployeeSalary, getEmployeeSalary } from '../../api/payroll';
import { getUsers } from '../../api/auth';
import { getBranches } from '../../api/branches';
import PeriodFilter from '../../components/common/PeriodFilter';
import BlurredValue from '../../components/common/BlurredValue';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';

const EmployeeSalariesPage = () => {
  const navigate = useNavigate();
  const { periodRange, getDateRangeParams } = useFiscalPeriod();
  const [salaries, setSalaries] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isNonUser, setIsNonUser] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRangeParams();
      const [salariesResponse, usersResponse, branchesResponse] = await Promise.all([
        getAllEmployeeSalaries({ search, module: 'retail', startDate, endDate }),
        getUsers(),
        getBranches({ limit: 100 })
      ]);
      // Extract data arrays from API responses (backend returns {success: true, data: [...]})
      const salariesArray = Array.isArray(salariesResponse)
        ? salariesResponse
        : salariesResponse?.data || salariesResponse?.salaries || [];
      const usersArray = Array.isArray(usersResponse)
        ? usersResponse
        : usersResponse?.users || usersResponse?.data || [];
      const branchesArray = Array.isArray(branchesResponse)
        ? branchesResponse
        : branchesResponse?.data || branchesResponse?.branches || [];
      setSalaries(salariesArray);
      setAllUsers(usersArray);
      setBranches(branchesArray);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [search, getDateRangeParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData, periodRange]);

  const handleAddNew = () => {
    setSelectedUser(null);
    setIsNonUser(false);
    setFormData({
      basic_salary: '',
      housing_allowance: '',
      transport_allowance: '',
      meal_allowance: '',
      utility_allowance: '',
      other_allowances: '',
      pension_contribution_rate: 8,
      employer_pension_rate: 10,
      pension_pin: '',
      pfa_name: '',
      tax_identification_number: '',
      bank_name: '',
      account_number: '',
      account_name: '',
      is_active: true,
      employee_name: '',
      employee_email: '',
      employee_branch_id: ''
    });
    setShowModal(true);
  };

  const handleEdit = async (salaryId, userId) => {
    try {
      const salary = await getEmployeeSalary(userId || `non-user-${salaryId}`);
      if (userId) {
        const user = allUsers.find((u) => u.id === userId);
        setSelectedUser(user);
        setIsNonUser(false);
      } else {
        setSelectedUser({
          id: salary.id,
          full_name: salary.employee_name,
          email: salary.employee_email
        });
        setIsNonUser(true);
      }
      setFormData({
        basic_salary: salary.basic_salary || '',
        housing_allowance: salary.housing_allowance || '',
        transport_allowance: salary.transport_allowance || '',
        meal_allowance: salary.meal_allowance || '',
        utility_allowance: salary.utility_allowance || '',
        other_allowances: salary.other_allowances || '',
        pension_contribution_rate: salary.pension_contribution_rate || 8,
        employer_pension_rate: salary.employer_pension_rate || 10,
        pension_pin: salary.pension_pin || '',
        pfa_name: salary.pfa_name || '',
        tax_identification_number: salary.tax_identification_number || '',
        bank_name: salary.bank_name || '',
        account_number: salary.account_number || '',
        account_name: salary.account_name || '',
        is_active: salary.is_active ?? true,
        employee_name: salary.employee_name || '',
        employee_email: salary.employee_email || '',
        employee_branch_id: salary.employee_branch_id || ''
      });
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load salary details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isNonUser && !selectedUser && !formData.user_id) {
      toast.error('Please select an employee');
      return;
    }
    if (isNonUser && !formData.employee_name) {
      toast.error('Staff name is required');
      return;
    }
    if (!formData.basic_salary) {
      toast.error('Basic salary is required');
      return;
    }

    setSaving(true);
    try {
      const userId = isNonUser
        ? selectedUser
          ? `non-user-${selectedUser.id}`
          : 'non-user'
        : selectedUser?.id || selectedUser?.user_id || formData.user_id;

      const payload = {
        ...formData,
        module: 'retail',
        employee_name: isNonUser ? formData.employee_name : null,
        employee_email: isNonUser ? formData.employee_email : null,
        employee_branch_id: isNonUser ? formData.employee_branch_id : null,
        user_id: isNonUser ? null : selectedUser?.id || selectedUser?.user_id || formData.user_id
      };

      await upsertEmployeeSalary(userId, payload);
      toast.success('Salary saved successfully');
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save salary');
    } finally {
      setSaving(false);
    }
  };

  // Get users without salary config for "Add New"
  const usersWithoutSalary = allUsers.filter(
    (user) => !salaries.find((s) => s.user_id === user.id)
  );

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/payroll')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                  Employee Salaries
                </h1>
                <p className="text-sm text-gray-500">Manage salary configurations</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <PeriodFilter storageKey="employee_salaries" />
              <button
                onClick={handleAddNew}
                className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark"
              >
                <Plus className="w-4 h-4" />
                Add Employee
              </button>
              <button onClick={fetchData} className="p-2 border rounded-lg hover:bg-gray-50">
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search employees..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
            />
          </div>
        </div>

        {/* Salaries Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-accent-gold border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : salaries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Employee</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Branch</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Basic</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Gross</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Pension %</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {salaries.map((salary) => (
                    <tr
                      key={salary.id}
                      onClick={() =>
                        navigate(
                          `/payroll/salaries/${salary.user_id ? salary.user_id : 'non-user-' + salary.id}`
                        )
                      }
                      className="hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-accent-gold hover:underline">
                            {salary.full_name}
                          </p>
                          <p className="text-sm text-gray-500">{salary.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {salary.branch_name || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <BlurredValue value={formatCurrency(salary.basic_salary)} />
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-accent-gold">
                        <BlurredValue value={formatCurrency(salary.gross_salary)} />
                      </td>
                      <td className="px-4 py-3 text-center text-sm">
                        {salary.pension_contribution_rate}% / {salary.employer_pension_rate}%
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                            salary.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {salary.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No employee salaries configured</p>
              <button
                onClick={handleAddNew}
                className="mt-4 px-4 py-2 bg-accent-gold text-primary-black rounded-lg"
              >
                Add First Employee
              </button>
            </div>
          )}
        </div>

        {/* Salary Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  {selectedUser ? `Edit Salary: ${selectedUser.full_name}` : 'Add Employee Salary'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {/* Employee Select or Non-User Inputs */}
                {!selectedUser ? (
                  <>
                    <div className="flex gap-4 p-1 bg-gray-100 dark:bg-slate-800 rounded-lg mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setIsNonUser(false);
                          setFormData((prev) => ({
                            ...prev,
                            user_id: '',
                            employee_name: '',
                            employee_email: '',
                            employee_branch_id: ''
                          }));
                        }}
                        className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          !isNonUser
                            ? 'bg-white dark:bg-slate-900 shadow-sm text-primary-black dark:text-white'
                            : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-300'
                        }`}
                      >
                        Link App User Account
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsNonUser(true);
                          setFormData((prev) => ({
                            ...prev,
                            user_id: 'non-user',
                            employee_name: '',
                            employee_email: '',
                            employee_branch_id: ''
                          }));
                        }}
                        className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          isNonUser
                            ? 'bg-white dark:bg-slate-900 shadow-sm text-primary-black dark:text-white'
                            : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-300'
                        }`}
                      >
                        Create Non-User Staff
                      </button>
                    </div>

                    {!isNonUser ? (
                      <div>
                        <label className="block text-sm font-medium mb-1">Select Employee *</label>
                        <select
                          value={formData.user_id || ''}
                          onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-700"
                          required
                        >
                          <option value="">-- Select Employee --</option>
                          {usersWithoutSalary.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.full_name} ({user.email})
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-1">Staff Name *</label>
                            <input
                              type="text"
                              value={formData.employee_name || ''}
                              onChange={(e) =>
                                setFormData({ ...formData, employee_name: e.target.value })
                              }
                              placeholder="e.g. John Doe"
                              className="w-full px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-700"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              Email (Optional)
                            </label>
                            <input
                              type="email"
                              value={formData.employee_email || ''}
                              onChange={(e) =>
                                setFormData({ ...formData, employee_email: e.target.value })
                              }
                              placeholder="e.g. john@company.com"
                              className="w-full px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-700"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Assign Branch *</label>
                          <select
                            value={formData.employee_branch_id || ''}
                            onChange={(e) =>
                              setFormData({ ...formData, employee_branch_id: e.target.value })
                            }
                            className="w-full px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-700"
                            required
                          >
                            <option value="">-- Select Branch --</option>
                            {branches.map((b) => (
                              <option key={b.id} value={b.id}>
                                {b.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  isNonUser && (
                    <div className="space-y-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                      <h4 className="font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300">
                        Staff Info (Non-User Profile)
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Staff Name *</label>
                          <input
                            type="text"
                            value={formData.employee_name || ''}
                            onChange={(e) =>
                              setFormData({ ...formData, employee_name: e.target.value })
                            }
                            className="w-full px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-700"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Email (Optional)</label>
                          <input
                            type="email"
                            value={formData.employee_email || ''}
                            onChange={(e) =>
                              setFormData({ ...formData, employee_email: e.target.value })
                            }
                            className="w-full px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-700"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Assign Branch *</label>
                        <select
                          value={formData.employee_branch_id || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, employee_branch_id: e.target.value })
                          }
                          className="w-full px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-700"
                          required
                        >
                          <option value="">-- Select Branch --</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )
                )}

                {/* Salary Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Basic Salary *</label>
                    <input
                      type="number"
                      value={formData.basic_salary}
                      onChange={(e) => setFormData({ ...formData, basic_salary: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Housing Allowance</label>
                    <input
                      type="number"
                      value={formData.housing_allowance}
                      onChange={(e) =>
                        setFormData({ ...formData, housing_allowance: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Transport Allowance</label>
                    <input
                      type="number"
                      value={formData.transport_allowance}
                      onChange={(e) =>
                        setFormData({ ...formData, transport_allowance: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Meal Allowance</label>
                    <input
                      type="number"
                      value={formData.meal_allowance}
                      onChange={(e) => setFormData({ ...formData, meal_allowance: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Utility Allowance</label>
                    <input
                      type="number"
                      value={formData.utility_allowance}
                      onChange={(e) =>
                        setFormData({ ...formData, utility_allowance: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Other Allowances</label>
                    <input
                      type="number"
                      value={formData.other_allowances}
                      onChange={(e) =>
                        setFormData({ ...formData, other_allowances: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Pension */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Pension Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Employee Rate (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.pension_contribution_rate}
                        onChange={(e) =>
                          setFormData({ ...formData, pension_contribution_rate: e.target.value })
                        }
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Employer Rate (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.employer_pension_rate}
                        onChange={(e) =>
                          setFormData({ ...formData, employer_pension_rate: e.target.value })
                        }
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">RSA PIN</label>
                      <input
                        type="text"
                        value={formData.pension_pin}
                        onChange={(e) => setFormData({ ...formData, pension_pin: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="PEN..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">PFA Name</label>
                      <input
                        type="text"
                        value={formData.pfa_name}
                        onChange={(e) => setFormData({ ...formData, pfa_name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="Pension Fund Administrator"
                      />
                    </div>
                  </div>
                </div>

                {/* Tax */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium mb-1">
                    Tax Identification Number (TIN)
                  </label>
                  <input
                    type="text"
                    value={formData.tax_identification_number}
                    onChange={(e) =>
                      setFormData({ ...formData, tax_identification_number: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="TIN..."
                  />
                </div>

                {/* Status & Module */}
                <div className="border-t pt-4 grid grid-cols-2 gap-4 items-center">
                  <div>
                    <label className="block text-sm font-medium mb-1">Payroll Module</label>
                    <input
                      type="text"
                      value="Retail"
                      className="w-full px-3 py-2 border rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-500 cursor-not-allowed font-semibold"
                      disabled
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active ?? true}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                      Active Profile (Include in payroll runs)
                    </label>
                  </div>
                </div>

                {/* Bank Details */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Bank Details</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Bank Name</label>
                      <input
                        type="text"
                        value={formData.bank_name}
                        onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Account Number</label>
                        <input
                          type="text"
                          value={formData.account_number}
                          onChange={(e) =>
                            setFormData({ ...formData, account_number: e.target.value })
                          }
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Account Name</label>
                        <input
                          type="text"
                          value={formData.account_name}
                          onChange={(e) =>
                            setFormData({ ...formData, account_name: e.target.value })
                          }
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Salary'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeSalariesPage;
